/**
 * Calendário de trabalho: converte tempo de relógio em tempo de fábrica.
 *
 * Serve para duas coisas que aparecem na tela o dia inteiro: o atraso de uma OS
 * e o "cabe até dia X". Contar horas corridas daria números errados com cara de
 * certos — uma OS emitida sexta às 17h com 4 h de trabalho não atrasa no sábado.
 *
 * **Fuso.** O sistema opera numa gráfica em Três Rios (RJ). O Brasil não tem
 * horário de verão desde 2019, então um deslocamento fixo de −03:00 é correto e
 * torna toda conta determinística — independente do fuso da máquina que roda o
 * código ou o teste. Se um dia a regra mudar, muda-se `DESLOCAMENTO_MIN`.
 */

import type { Calendario, DiaCalendario } from './tipos.js';

/** Minutos de deslocamento em relação ao UTC. −180 = UTC−03:00. */
export const DESLOCAMENTO_MIN = -180;

export interface ParteDoTempo {
  ano: number;
  mes: number;
  dia: number;
  hora: number;
  minuto: number;
  segundo: number;
  /** 0 = domingo. */
  diaSemana: number;
}

const DIA_MS = 86_400_000;

/** Quebra um instante ISO nas partes do relógio de parede da gráfica. */
export function partes(iso: string, deslocamentoMin = DESLOCAMENTO_MIN): ParteDoTempo {
  const d = new Date(Date.parse(iso) + deslocamentoMin * 60_000);
  return {
    ano: d.getUTCFullYear(),
    mes: d.getUTCMonth() + 1,
    dia: d.getUTCDate(),
    hora: d.getUTCHours(),
    minuto: d.getUTCMinutes(),
    segundo: d.getUTCSeconds(),
    diaSemana: d.getUTCDay(),
  };
}

/** "AAAA-MM-DD" do instante, no relógio da gráfica. */
export function dataLocal(iso: string, deslocamentoMin = DESLOCAMENTO_MIN): string {
  const p = partes(iso, deslocamentoMin);
  return `${p.ano}-${String(p.mes).padStart(2, '0')}-${String(p.dia).padStart(2, '0')}`;
}

/** Instante ISO de uma data e hora do relógio da gráfica. */
export function instante(
  data: string,
  hhmm: string,
  deslocamentoMin = DESLOCAMENTO_MIN,
): string {
  const [a, m, d] = data.split('-').map(Number);
  const [h, min] = hhmm.split(':').map(Number);
  const utc = Date.UTC(a ?? 1970, (m ?? 1) - 1, d ?? 1, h ?? 0, min ?? 0) - deslocamentoMin * 60_000;
  return new Date(utc).toISOString();
}

function minutosDe(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Os intervalos produtivos de um dia, em minutos desde a meia-noite. */
function janelasDoDia(dia: DiaCalendario): Array<[number, number]> {
  if (!dia.trabalha) return [];
  const ini = minutosDe(dia.inicio);
  const fim = minutosDe(dia.fim);
  if (fim <= ini) return [];
  if (!dia.pausaInicio || !dia.pausaFim) return [[ini, fim]];
  const pi = Math.max(ini, minutosDe(dia.pausaInicio));
  const pf = Math.min(fim, minutosDe(dia.pausaFim));
  if (pf <= pi) return [[ini, fim]];
  const saida: Array<[number, number]> = [];
  if (pi > ini) saida.push([ini, pi]);
  if (fim > pf) saida.push([pf, fim]);
  return saida;
}

/**
 * Segundos de trabalho entre dois instantes.
 *
 * Caminha dia a dia. O horizonte é limitado a 400 dias: uma previsão de entrega
 * muito distante (ou uma data digitada errada) não pode fazer a interface
 * pendurar num laço.
 */
export function segundosUteisEntre(
  de: string,
  ate: string,
  calendario: Calendario,
  deslocamentoMin = DESLOCAMENTO_MIN,
): number {
  const inicio = Date.parse(de);
  const fim = Date.parse(ate);
  if (!(fim > inicio)) return 0;

  const feriados = new Set(calendario.feriados);
  const porDiaSemana = new Map(calendario.dias.map((d) => [d.diaSemana, d]));

  let total = 0;
  let cursor = inicio;
  let voltas = 0;

  while (cursor < fim && voltas < 400) {
    voltas += 1;
    const data = dataLocal(new Date(cursor).toISOString(), deslocamentoMin);
    const p = partes(new Date(cursor).toISOString(), deslocamentoMin);
    const dia = porDiaSemana.get(p.diaSemana);

    if (dia && !feriados.has(data)) {
      for (const [ji, jf] of janelasDoDia(dia)) {
        const a = Date.parse(instante(data, minutosParaHhmm(ji), deslocamentoMin));
        const b = Date.parse(instante(data, minutosParaHhmm(jf), deslocamentoMin));
        const ini = Math.max(a, cursor, inicio);
        const f = Math.min(b, fim);
        if (f > ini) total += f - ini;
      }
    }

    // Pula para a meia-noite seguinte, no relógio da gráfica.
    const proxima = Date.parse(instante(data, '00:00', deslocamentoMin)) + DIA_MS;
    cursor = proxima > cursor ? proxima : cursor + DIA_MS;
  }

  return Math.round(total / 1000);
}

function minutosParaHhmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** A gráfica está trabalhando neste instante? */
export function dentroDoExpediente(
  iso: string,
  calendario: Calendario,
  deslocamentoMin = DESLOCAMENTO_MIN,
): boolean {
  const p = partes(iso, deslocamentoMin);
  const data = dataLocal(iso, deslocamentoMin);
  if (calendario.feriados.includes(data)) return false;
  const dia = calendario.dias.find((d) => d.diaSemana === p.diaSemana);
  if (!dia) return false;
  const agora = p.hora * 60 + p.minuto;
  return janelasDoDia(dia).some(([a, b]) => agora >= a && agora < b);
}

/**
 * O fim do expediente do dia deste instante. Nulo se o dia não é de trabalho.
 * É o que a rotina de encerramento automático usa (RN-022).
 */
export function fimDoExpediente(
  iso: string,
  calendario: Calendario,
  deslocamentoMin = DESLOCAMENTO_MIN,
): string | null {
  const p = partes(iso, deslocamentoMin);
  const data = dataLocal(iso, deslocamentoMin);
  if (calendario.feriados.includes(data)) return null;
  const dia = calendario.dias.find((d) => d.diaSemana === p.diaSemana);
  if (!dia || !dia.trabalha) return null;
  return instante(data, dia.fim, deslocamentoMin);
}

/** Calendário de partida (PRD §17.1 J-12 — a confirmar com a gráfica). */
export function calendarioDePartida(): Calendario {
  const util = (diaSemana: number): DiaCalendario => ({
    diaSemana,
    trabalha: true,
    inicio: '08:00',
    fim: '18:00',
    pausaInicio: '12:00',
    pausaFim: '13:00',
  });
  const folga = (diaSemana: number): DiaCalendario => ({
    diaSemana,
    trabalha: false,
    inicio: '00:00',
    fim: '00:00',
    pausaInicio: null,
    pausaFim: null,
  });
  return {
    dias: [folga(0), util(1), util(2), util(3), util(4), util(5), folga(6)],
    feriados: [],
  };
}
